import { useState } from 'react';
import { AsyncBoundary, Notice } from '@kollez/design-system';
import { usePrograms } from '@/lib/queries';

/**
 * /group/programs (Page 75/N) — programme catalog (B.Tech, MBA, MCA,
 * M.Tech, B.Sc, Diploma, PhD, …). Tenant-scoped; reference-data seeds
 * union with tenant-owned rows.
 */

export function GroupProgramsPage() {
  const [level, setLevel] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const list = usePrograms({ level, status, q, sortBy: 'name', sortDir: 'asc', limit: 200 });
  const rows = list.data?.rows ?? [];

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Institutions · Catalog</p>
          <h1 className="h-page">Programmes</h1>
          <p className="subhead">
            Every programme (B.Tech, MBA, MCA, M.Tech, B.Sc, Diploma, PhD, …) offered
            across your colleges. Branches live one level down at{' '}
            <a className="link" href="/group/specializations">Branches</a>.
          </p>
        </div>
      </div>

      <div className="card">
        <div className="filterbar">
          <input className="input" placeholder="Search programme name or code" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="select" value={level} onChange={(e) => setLevel(e.target.value)}>
            <option value="">Any level</option>
            <option value="UG">UG</option>
            <option value="PG">PG</option>
            <option value="Diploma">Diploma</option>
            <option value="Doctorate">Doctorate</option>
            <option value="Certificate">Certificate</option>
          </select>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="phasing_out">Phasing out</option>
          </select>
          <span className="fb-count">{list.data?.meta?.total ?? rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No programmes in scope"
          emptyHint="Loosen the filters, or add one — it'll surface across your colleges."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Level</th>
                  <th>Duration</th>
                  <th>AISHE</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id}>
                    <td className="cell-title">{r.name}</td>
                    <td className="cell-sub">{r.code ?? '—'}</td>
                    <td className="cell-sub">{r.level ?? '—'}</td>
                    <td className="cell-sub">{r.durationYears ? `${r.durationYears}y` : '—'}</td>
                    <td className="cell-sub">{r.aisheCode ?? '—'}</td>
                    <td><span className="badge">{r.status ?? '—'}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>Programmes are the parent of branches.</b> B.Tech is a programme; CSE / ECE /
        Mech are branches under B.Tech at a specific college.
      </Notice>
    </div>
  );
}
